<script setup>
import { useApi } from '@/utils/api'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const route = useRoute()

const loading = ref(true)
const notFound = ref(false)
const registration = ref(null)

// Status yang masih bisa berubah -> terus di-poll. Status final
// (approved/rejected) berhenti poll, tidak ada gunanya lagi.
// pending_verification (Addendum 13, Modul BE) ikut dipoll -- masih
// menunggu admin cek manual mutasi rekening (Modul BF), bisa berubah kapan
// saja tanpa webhook.
const POLLING_STATUSES = ['pending_payment', 'pending_verification', 'paid_pending_approval']
let pollTimer = null

const fetchStatus = async () => {
  try {
    const response = await useApi(`/api/public/registrations/${route.params.registrationCode}`).get()
    const body = response?.data ?? response

    registration.value = body?.data ?? body
    notFound.value = false
  } catch (err) {
    // 404 generik -- kode salah format ATAU valid tapi tidak ada, keduanya
    // harus terlihat sama dari luar (tidak boleh dipakai enumerasi).
    notFound.value = true
    registration.value = null
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await fetchStatus()

  if (registration.value && POLLING_STATUSES.includes(registration.value.status)) {
    pollTimer = setInterval(async () => {
      await fetchStatus()
      if (registration.value && !POLLING_STATUSES.includes(registration.value.status) && pollTimer) {
        clearInterval(pollTimer)
        pollTimer = null
      }
    }, 8000)
  }
})

onBeforeUnmount(() => {
  if (pollTimer) clearInterval(pollTimer)
})

const statusMeta = computed(() => {
  const status = registration.value?.status
  
  return {
    pending_payment: {
      icon: 'ri-time-line',
      color: 'warning',
      title: 'Menunggu Pembayaran',
      desc: 'Pendaftaran Anda tersimpan. Silakan selesaikan pembayaran melalui link yang dikirimkan sebelumnya.',
    },
    pending_verification: {
      icon: 'ri-file-search-line',
      color: 'info',
      title: 'Menunggu Verifikasi Pembayaran',
      desc: 'Bukti transfer Anda sudah kami terima. Tim kami akan mencocokkan dengan mutasi rekening dan mengaktifkan akun toko Anda sesegera mungkin.',
    },
    paid_pending_approval: {
      icon: 'ri-checkbox-circle-line',
      color: 'info',
      title: 'Sudah Dibayar — Menunggu Persetujuan',
      desc: 'Pembayaran Anda sudah kami terima. Tim kami akan meninjau dan mengaktifkan akun toko Anda sesegera mungkin.',
    },
    approved: {
      icon: 'ri-verified-badge-line',
      color: 'success',
      title: 'Pendaftaran Diterima!',
      desc: 'Akun toko Anda sudah aktif. Silakan masuk untuk mulai menggunakan PintuKasir.',
    },
    rejected: {
      icon: 'ri-close-circle-line',
      color: 'error',
      title: 'Pendaftaran Ditolak',
      desc: 'Mohon maaf, pendaftaran Anda tidak dapat kami proses. Hubungi tim kami untuk informasi lebih lanjut.',
    },
    expired: {
      icon: 'ri-error-warning-line',
      color: 'error',
      title: 'Invoice Kedaluwarsa',
      desc: 'Link pembayaran untuk pendaftaran ini sudah kedaluwarsa. Silakan daftar ulang.',
    },
  }[status] ?? {
    icon: 'ri-question-line',
    color: 'default',
    title: 'Status Tidak Diketahui',
    desc: '',
  }
})
</script>

<template>
  <div class="status-screen">
    <VContainer class="py-10">
      <VRow justify="center">
        <VCol
          cols="12"
          sm="8"
          md="6"
          lg="5"
        >
          <div class="text-center mb-6">
            <RouterLink
              to="/"
              class="text-decoration-none"
            >
              <h5 class="text-h5 font-weight-bold text-primary mb-0">
                PintuKasir
              </h5>
            </RouterLink>
          </div>

          <VCard
            v-if="loading"
            rounded="xl"
            elevation="4"
          >
            <VCardText class="pa-10 text-center">
              <VProgressCircular
                indeterminate
                color="primary"
              />
            </VCardText>
          </VCard>

          <VCard
            v-else-if="notFound"
            rounded="xl"
            elevation="4"
          >
            <VCardText class="pa-8 text-center">
              <VIcon
                icon="ri-file-search-line"
                size="48"
                color="disabled"
                class="mb-3"
              />
              <h6 class="text-h6 font-weight-bold mb-2">
                Pendaftaran Tidak Ditemukan
              </h6>
              <p class="text-body-2 text-medium-emphasis mb-0">
                Periksa kembali link yang Anda gunakan, atau daftar ulang jika diperlukan.
              </p>
              <VBtn
                :to="{ name: 'daftar' }"
                variant="tonal"
                class="mt-5"
              >
                Daftar Sekarang
              </VBtn>
            </VCardText>
          </VCard>

          <VCard
            v-else
            rounded="xl"
            elevation="4"
          >
            <VCardText class="pa-8 text-center">
              <VAvatar
                :color="statusMeta.color"
                variant="tonal"
                size="64"
                class="mb-4"
              >
                <VIcon
                  :icon="statusMeta.icon"
                  size="34"
                />
              </VAvatar>

              <h6 class="text-h6 font-weight-bold mb-2">
                {{ statusMeta.title }}
              </h6>
              <p class="text-body-2 text-medium-emphasis mb-1">
                {{ registration.business_name }}
              </p>
              <p
                v-if="registration.plan?.name"
                class="text-caption text-medium-emphasis mb-4"
              >
                Paket {{ registration.plan.name }}
              </p>

              <p class="text-body-2 mt-4 mb-5">
                {{ statusMeta.desc }}
              </p>

              <VBtn
                v-if="registration.status === 'approved'"
                :to="{ name: 'login' }"
                color="success"
                size="large"
                block
              >
                Masuk ke PintuKasir
                <VIcon
                  icon="ri-arrow-right-line"
                  end
                />
              </VBtn>

              <div
                v-if="POLLING_STATUSES.includes(registration.status)"
                class="d-flex align-center justify-center gap-2 mt-5"
              >
                <span class="poll-dot" />
                <span class="text-caption text-medium-emphasis">Halaman ini otomatis diperbarui</span>
              </div>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
    </VContainer>
  </div>
</template>

<style scoped>
.status-screen {
  min-block-size: 100vh;
  background: rgba(var(--v-theme-on-surface), 0.02);
}

.poll-dot {
  inline-size: 8px;
  block-size: 8px;
  border-radius: 50%;
  background: rgb(var(--v-theme-primary));
  animation: poll-pulse 1.6s ease-in-out infinite;
}

@keyframes poll-pulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .poll-dot { animation: none; }
}
</style>